import {
  type PlayerIndex,
  type PlayerInputs,
  TOUCH_JOYSTICK_DEAD_ZONE,
  TOUCH_JOYSTICK_RADIUS_PX,
  TOUCH_VIEWPORT_MAX_WIDTH_PX,
} from '@md/shared';

/**
 * Contrôles tactiles en surimpression (concept §5.2) : joystick à gauche,
 * boutons SAUT et CHAMP à droite.
 *
 * Ils s'activent sur un **pointeur grossier** (doigt) ou sur un **petit
 * viewport** — les deux critères du prompt de phase. Sur un écran tactile de
 * bureau (2-en-1), les deux modes d'entrée restent disponibles : le clavier
 * n'est jamais débranché.
 */
export interface TouchControls {
  readonly element: HTMLElement;
  /** Ré-évalue l'activation (redimensionnement, changement d'écran). */
  refresh(): void;
  /** Change le robot piloté (mode réseau : le personnage du joueur local). */
  setPlayer(player: PlayerIndex): void;
  attach(): void;
  detach(): void;
  enabled(): boolean;
}

export interface TouchControlsOptions {
  readonly onInput: (player: PlayerIndex, patch: Partial<PlayerInputs>) => void;
  readonly player?: PlayerIndex;
  /** Fenêtre observée — injectable pour les tests unitaires. */
  readonly view?: Pick<Window, 'matchMedia' | 'innerWidth' | 'navigator'>;
}

/**
 * Faut-il afficher les contrôles tactiles ?
 *
 * Vrai si le pointeur principal est grossier (`pointer: coarse`), si l'appareil
 * rapporte des points de contact, ou si le viewport est étroit.
 */
export function shouldEnableTouchControls(
  view: Pick<Window, 'matchMedia' | 'innerWidth' | 'navigator'> = window,
): boolean {
  const coarse = view.matchMedia?.('(pointer: coarse)').matches === true;
  const touchPoints = (view.navigator?.maxTouchPoints ?? 0) > 0;
  const narrow = view.innerWidth <= TOUCH_VIEWPORT_MAX_WIDTH_PX;
  return coarse || touchPoints || narrow;
}

/** Traduction d'un écart de doigt (px depuis le centre) en intention de jeu. */
export interface JoystickReading {
  readonly left: boolean;
  readonly right: boolean;
  /** Axe analogique remappé : 0 au bord de la zone morte, ±1 au rayon complet. */
  readonly moveX: number;
  /** Position visuelle du bouton, dans [-1, 1] (avant remappage). */
  readonly knob: number;
}

/**
 * Vitesse proportionnelle au déplacement du doigt : la zone morte est
 * soustraite puis l'axe est remappé sur [0, 1], pour démarrer doucement dès
 * qu'on la quitte au lieu de sauter de l'arrêt à la pleine vitesse.
 */
export function readJoystick(dxPx: number): JoystickReading {
  const knob = Math.max(-1, Math.min(1, dxPx / TOUCH_JOYSTICK_RADIUS_PX));
  const magnitude = Math.abs(knob);
  const eased =
    magnitude <= TOUCH_JOYSTICK_DEAD_ZONE
      ? 0
      : (magnitude - TOUCH_JOYSTICK_DEAD_ZONE) / (1 - TOUCH_JOYSTICK_DEAD_ZONE);
  // `eased === 0` court-circuite le `-0` que produirait `Math.sign` côté gauche.
  const moveX = eased === 0 ? 0 : Math.sign(knob) * eased;
  return { left: moveX < 0, right: moveX > 0, moveX, knob };
}

function el<K extends keyof HTMLElementTagNameMap>(
  tag: K,
  className: string,
  text?: string,
): HTMLElementTagNameMap[K] {
  const node = document.createElement(tag);
  node.className = className;
  if (text !== undefined) {
    node.textContent = text;
  }
  return node;
}

export function createTouchControls(options: TouchControlsOptions): TouchControls {
  const view = options.view ?? window;
  let player: PlayerIndex = options.player ?? 0;
  let active = false;

  const element = el('div', 'touch');
  element.dataset.testid = 'touch-controls';
  element.hidden = true;

  // --- Joystick ------------------------------------------------------------
  const joystick = el('div', 'touch__joystick');
  joystick.dataset.testid = 'touch-joystick';
  const knob = el('div', 'touch__knob');
  joystick.append(knob);

  // --- Boutons -------------------------------------------------------------
  const jump = el('button', 'touch__button touch__button--jump', 'SAUT');
  jump.type = 'button';
  jump.dataset.testid = 'touch-jump';
  const field = el('button', 'touch__button touch__button--field', '🧲 CHAMP');
  field.type = 'button';
  field.dataset.testid = 'touch-field';

  const buttons = el('div', 'touch__buttons');
  buttons.append(jump, field);
  element.append(joystick, buttons);

  let joystickPointer: number | null = null;

  const setDirection = (dx: number): void => {
    const reading = readJoystick(dx);
    joystick.dataset.left = String(reading.left);
    joystick.dataset.right = String(reading.right);
    knob.style.transform = `translateX(${reading.knob * TOUCH_JOYSTICK_RADIUS_PX * 0.5}px)`;
    options.onInput(player, { left: reading.left, right: reading.right, moveX: reading.moveX });
  };

  /**
   * `emit = false` remet l'interface au repos SANS prévenir la session : c'est
   * ce qu'il faut au changement de robot piloté, où l'ancien joueur n'existe
   * plus dans la nouvelle session (le mode réseau refuserait l'entrée).
   */
  const releaseJoystick = (emit = true): void => {
    joystickPointer = null;
    joystick.dataset.left = 'false';
    joystick.dataset.right = 'false';
    knob.style.transform = 'translateX(0px)';
    if (emit) {
      options.onInput(player, { left: false, right: false, moveX: 0 });
    }
  };

  const onJoystickDown = (event: PointerEvent): void => {
    event.preventDefault();
    joystickPointer = event.pointerId;
    joystick.setPointerCapture?.(event.pointerId);
    const rect = joystick.getBoundingClientRect();
    setDirection(event.clientX - (rect.left + rect.width / 2));
  };

  const onJoystickMove = (event: PointerEvent): void => {
    if (joystickPointer !== event.pointerId) {
      return;
    }
    event.preventDefault();
    const rect = joystick.getBoundingClientRect();
    setDirection(event.clientX - (rect.left + rect.width / 2));
  };

  const onJoystickUp = (event: PointerEvent): void => {
    if (joystickPointer !== event.pointerId) {
      return;
    }
    event.preventDefault();
    releaseJoystick();
  };

  const bindButton = (node: HTMLElement, action: keyof PlayerInputs): void => {
    const press = (event: PointerEvent): void => {
      event.preventDefault();
      node.dataset.pressed = 'true';
      node.setPointerCapture?.(event.pointerId);
      options.onInput(player, { [action]: true } as Partial<PlayerInputs>);
    };
    const release = (event: PointerEvent): void => {
      event.preventDefault();
      node.dataset.pressed = 'false';
      options.onInput(player, { [action]: false } as Partial<PlayerInputs>);
    };
    node.addEventListener('pointerdown', press);
    node.addEventListener('pointerup', release);
    node.addEventListener('pointercancel', release);
    node.addEventListener('pointerleave', release);
  };

  bindButton(jump, 'jump');
  bindButton(field, 'action');

  const refresh = (): void => {
    active = shouldEnableTouchControls(view);
    element.hidden = !active;
    element.dataset.enabled = String(active);
  };

  return {
    element,
    refresh,
    setPlayer(next: PlayerIndex): void {
      if (next === player) {
        return;
      }
      releaseJoystick(false);
      jump.dataset.pressed = 'false';
      field.dataset.pressed = 'false';
      player = next;
    },
    attach(): void {
      joystick.addEventListener('pointerdown', onJoystickDown);
      joystick.addEventListener('pointermove', onJoystickMove);
      joystick.addEventListener('pointerup', onJoystickUp);
      joystick.addEventListener('pointercancel', onJoystickUp);
      refresh();
    },
    detach(): void {
      joystick.removeEventListener('pointerdown', onJoystickDown);
      joystick.removeEventListener('pointermove', onJoystickMove);
      joystick.removeEventListener('pointerup', onJoystickUp);
      joystick.removeEventListener('pointercancel', onJoystickUp);
      releaseJoystick();
    },
    enabled(): boolean {
      return active;
    },
  };
}
